/**
 * src/services/spatial/map/mapChannels.ts
 *
 * Channel-name helpers for GeoLink's map delta bus (handoff §5).
 *
 * Channel grammar:  `map:<base>`  (public)
 *                   `map:<base>:<CC>`  (country-scoped, UPPER-case ISO 3166-1
 *                                        alpha-2)
 *
 * Bases are SINGULAR and camelCase — e.g. `map:agent:US`, NOT `map:agents:US`.
 * These constants are the single source of truth so builders/subscribers can't
 * drift from the wire format.
 */

/** Singular, camelCase channel bases GeoLink publishes on. */
export const MAP_CHANNEL_BASE = {
  spatialObject: 'spatialObject',
  executionRule: 'executionRule',
  ruleEvent: 'ruleEvent',
  agent: 'agent',
  liveSession: 'liveSession',
  presenceCell: 'presenceCell',
  drone: 'drone',
  policy: 'policy',
} as const;

export type MapChannelBase = (typeof MAP_CHANNEL_BASE)[keyof typeof MAP_CHANNEL_BASE];

/**
 * Build a channel string. Pass a 2-letter country code to scope the channel
 * (it is upper-cased); omit it for the public channel.
 */
export function mapChannel(base: MapChannelBase, countryCode?: string | null): string {
  const cc = countryCode && countryCode.trim() ? countryCode.trim().toUpperCase() : null;
  return cc ? `map:${base}:${cc}` : `map:${base}`;
}

/** Parse a channel string back into `{ base, countryCode }` (null cc = public). */
export function parseMapChannel(
  channel: string,
): { base: string; countryCode: string | null } | null {
  const m = /^map:([a-zA-Z]+)(?::([A-Za-z]{2}))?$/.exec(channel);
  if (!m) return null;
  return { base: m[1], countryCode: m[2] ? m[2].toUpperCase() : null };
}
